/**
 * Inline Attribution Panel — Progressive disclosure replacement for modal.
 * Expand in-place to reveal source-level financial decomposition.
 */
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Layers } from "lucide-react";
import type { FinancialAttribution } from "@/data/fie/attributionTypes";
import { getAttributionBreakdown } from "@/data/fie/attributionTypes";
import { Currency } from "./SharedComponents";
import { cn } from "@/lib/utils";

interface Props {
  attributions: FinancialAttribution[];
  label?: string;
  compact?: boolean;
}

export function InlineAttributionPanel({ attributions, label, compact = false }: Props) {
  const [expanded, setExpanded] = useState(false);

  if (!attributions || attributions.length === 0) return null;

  const breakdown = getAttributionBreakdown(attributions);

  return (
    <div className="w-full">
      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Layers className="h-3 w-3" />
        <span className="group-hover:underline">{label || "View Breakdown"}</span>
        <span className="text-[10px] text-muted-foreground/60">
          ({attributions.length} sources)
        </span>
      </button>

      {expanded && (
        <div className={cn(
          "mt-2 rounded-lg border bg-muted/30 overflow-hidden animate-in slide-in-from-top-1 duration-200",
          compact ? "p-2" : "p-3"
        )}>
          {/* Summary bar */}
          <div className="flex items-center gap-3 mb-3 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-success/60" />
              <span className="text-muted-foreground">Actual</span>
              <span className="font-medium"><Currency value={breakdown.totalActual} /></span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-warning/60" />
              <span className="text-muted-foreground">Estimated</span>
              <span className="font-medium"><Currency value={breakdown.totalEstimated} /></span>
            </div>
            <div className="ml-auto font-medium">
              <Currency value={breakdown.total} />
            </div>
          </div>

          {/* Attribution rows */}
          <div className="space-y-1">
            {attributions.map((a) => (
              <AttributionRow key={a.id} attribution={a} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AttributionRow({ attribution: a }: { attribution: FinancialAttribution }) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-2 rounded text-xs hover:bg-muted/50 transition-colors">
      {/* Status indicator */}
      <div className={cn(
        "h-2 w-2 rounded-full shrink-0",
        a.isActual ? "bg-success/60" : "bg-warning/60"
      )} />

      {/* Source label */}
      <Badge variant="outline" className="text-[9px] uppercase tracking-wider shrink-0 h-5">
        {a.sourceType}
      </Badge>

      {/* Description */}
      <span className="text-muted-foreground truncate flex-1">
        {a.description}
      </span>

      {/* Job link */}
      {a.jobId && (
        <span className="font-mono text-[10px] text-muted-foreground/60 shrink-0">
          {a.jobId}
        </span>
      )}

      {/* Amount */}
      <span className={cn(
        "font-medium tabular-nums shrink-0",
        a.isActual ? "text-success" : "text-warning"
      )}>
        <Currency value={a.amount} />
      </span>
    </div>
  );
}
